import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Plus, RefreshCw, X, Loader, Link2, QrCode, Search, ChevronLeft } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import { televisaoService, Televisao } from '../services/televisaoService';
import { bannerService, TelaBanner } from '../../banners/services/bannerService';
import { TelevisaoCard } from '../components/TelevisaoCard';
import { LinkScreensModal } from '../components/LinkScreensModal';
import { TelaMinima } from '../../banners/services/bannerService';
import { authService } from '../../auth/services/authService';

const TelevisoesManagementPage = () => {
    const [televisoes, setTelevisoes] = useState<Televisao[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [formData, setFormData] = useState({ nmTelevisao: '' });
    const [saving, setSaving] = useState(false);
    const [selectedTelevisaoForLink, setSelectedTelevisaoForLink] = useState<Televisao | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showLinkTotemModal, setShowLinkTotemModal] = useState(false);

    // Mapa de telas para busca rápida (apenas id e nome para busca inicial)
    const [telasMap, setTelasMap] = useState<Map<number, TelaMinima>>(new Map());

    // Carregar televisões
    const carregarTelevisoes = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await televisaoService.listarTelevisoes();
            
            // Garantir que result é um array válido
            if (!Array.isArray(result)) {
                setTelevisoes([]);
            } else {
                // Filtrar apenas televisões válidas (com cdTelevisao não null)
                const televisoesValidas = result.filter(tv => 
                    tv && 
                    tv.cdTelevisao != null && 
                    tv.nmTelevisao != null && 
                    tv.nmTelevisao.trim() !== ''
                );
                setTelevisoes(televisoesValidas);
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar televisões';
            setError(errorMessage);
            // Em caso de erro, definir array vazio para não quebrar a UI
            setTelevisoes([]);
        } finally {
            setLoading(false);
        }
    }, []);

    // Carregar telas disponíveis (apenas id e nome para busca inicial)
    const carregarTelas = useCallback(async () => {
        try {
            // OTIMIZAÇÃO: Usar método mínimo - apenas id e nome
            // Para busca inicial, só precisamos saber quais telas pertencem à televisão
            const telas = await bannerService.listarTelasMinimo();
            
            const map = new Map<number, TelaMinima>();
            telas.forEach(tela => {
                if (tela.id) {
                    map.set(tela.id, tela);
                }
            });
            setTelasMap(map);
        } catch (err) {
            console.error('Erro ao carregar telas:', err);
        }
    }, []);

    useEffect(() => {
        carregarTelevisoes();
        carregarTelas();
    }, [carregarTelevisoes, carregarTelas]);

    // Obter telas vinculadas para uma televisão (apenas id e nome)
    const getTelasVinculadas = useCallback((televisao: Televisao): TelaMinima[] => {
        const telaIds = televisaoService.stringToTelaIds(televisao.dsTelasCadastradas);
        return telaIds
            .map(id => telasMap.get(id))
            .filter((tela): tela is TelaMinima => tela !== undefined);
    }, [telasMap]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.nmTelevisao.trim()) {
            alert('Nome da televisão é obrigatório');
            return;
        }

        setSaving(true);
        try {
            if (editingId) {
                // Atualizar nome não está disponível na API atual, apenas telas cadastradas
                // Por enquanto, vamos apenas fechar o modal
                alert('A atualização do nome da televisão não está disponível no momento');
                setShowForm(false);
                setEditingId(null);
                setFormData({ nmTelevisao: '' });
            } else {
                const resultado = await televisaoService.criarTelevisao({
                    nmTelevisao: formData.nmTelevisao.trim()
                });
                if (resultado.cdTelevisao == null) {
                    throw new Error('A televisão foi criada mas não retornou um ID válido');
                }
                await carregarTelevisoes();
                setShowForm(false);
                setFormData({ nmTelevisao: '' });
            }
        } catch (error) {
            alert(`Erro: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (televisao: Televisao) => {
        if (televisao.cdTelevisao == null) {
            alert('Não é possível editar uma televisão sem ID');
            return;
        }
        setEditingId(televisao.cdTelevisao);
        setFormData({ nmTelevisao: televisao.nmTelevisao || '' });
        setShowForm(true);
    };

    const handleDelete = async (id: number | null) => {
        if (id == null) {
            alert('Não é possível excluir uma televisão sem ID');
            return;
        }
        if (!window.confirm('Tem certeza que deseja excluir esta televisão?')) return;

        try {
            await televisaoService.deletarTelevisao(id);
            await carregarTelevisoes();
        } catch (error) {
            alert(`Erro: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
        }
    };

    const handleLinkScreens = (televisao: Televisao) => {
        if (televisao.cdTelevisao == null) {
            alert('Não é possível vincular telas a uma televisão sem ID');
            return;
        }
        setSelectedTelevisaoForLink(televisao);
    };

    const handleSaveLinkScreens = async (televisaoId: number | null, telaIds: number[], temposTelas: Map<number, number>, ordensTelas: Map<number, number>, diasTelas: Map<number, string[]>) => {
        if (televisaoId == null) {
            alert('Erro: ID da televisão inválido');
            return;
        }

        // Buscar televisão atual para preservar configurações existentes
        const televisaoAtual = televisoes.find(tv => tv.cdTelevisao === televisaoId);
        let dsConfiguracoes = televisaoAtual?.dsConfiguracoes || null;

        // Atualizar tempos das telas no JSON de configurações
        // Remover tempos de telas que não estão mais selecionadas
        const temposAtualizados = new Map<number, number>();
        telaIds.forEach(telaId => {
            const tempo = temposTelas.get(telaId);
            if (tempo !== undefined && tempo > 0) {
                temposAtualizados.set(telaId, tempo);
            }
        });

        // Atualizar JSON de configurações com os novos tempos
        // Primeiro, remover tempos de telas que não estão mais selecionadas
        if (dsConfiguracoes) {
            try {
                const config = JSON.parse(dsConfiguracoes);
                // Remover tempos de telas que não estão mais na lista
                Object.keys(config).forEach(key => {
                    const telaId = parseInt(key, 10);
                    if (!isNaN(telaId) && !telaIds.includes(telaId)) {
                        delete config[key];
                    }
                });
                dsConfiguracoes = JSON.stringify(config);
            } catch (error) {
                console.warn('Erro ao processar configurações existentes, criando novo JSON:', error);
                dsConfiguracoes = null;
            }
        }

        // Adicionar/atualizar tempos, ordens e dias das telas selecionadas
        temposAtualizados.forEach((tempo, telaId) => {
            const ordem = ordensTelas.get(telaId) || 0;
            const dias = diasTelas.get(telaId) || [];
            dsConfiguracoes = (televisaoService as any).atualizarConfigTela(dsConfiguracoes, telaId, tempo, ordem, dias);
        });
        
        // Atualizar ordens e dias de telas que não têm tempo mas têm ordem
        ordensTelas.forEach((ordem, telaId) => {
            if (!temposAtualizados.has(telaId) && ordem > 0) {
                const dias = diasTelas.get(telaId) || [];
                dsConfiguracoes = (televisaoService as any).atualizarConfigTela(dsConfiguracoes, telaId, undefined, ordem, dias);
            }
        });
        
        // Atualizar dias de telas que não têm tempo nem ordem mas têm dias configurados
        diasTelas.forEach((dias, telaId) => {
            if (!temposAtualizados.has(telaId) && !ordensTelas.has(telaId) && dias.length > 0) {
                dsConfiguracoes = (televisaoService as any).atualizarConfigTela(dsConfiguracoes, telaId, undefined, undefined, dias);
            }
        });

        // Preparar comando de atualização
        const command: any = {};
        
        // Sempre enviar dsTelasCadastradas
        command.dsTelasCadastradas = televisaoService.telaIdsToString(telaIds);
        
        // IMPORTANTE: Sempre enviar dsConfiguracoes (mesmo que seja '{}' ou string vazia)
        // O backend espera o campo como string JSON
        if (dsConfiguracoes) {
            command.dsConfiguracoes = dsConfiguracoes;
        } else {
            // Se não houver configurações, enviar JSON vazio
            command.dsConfiguracoes = '{}';
        }

        const sucesso = await televisaoService.atualizarTelevisao(televisaoId, command);
        
        await carregarTelevisoes();
        setSelectedTelevisaoForLink(null);
    };

    // Filtrar televisões
    const filteredTelevisoes = useMemo(() => {
        if (!searchTerm.trim()) return televisoes;
        const searchLower = searchTerm.toLowerCase();
        return televisoes.filter(tv =>
            (tv.nmTelevisao || '').toLowerCase().includes(searchLower) ||
            (tv.cdTelevisao != null && tv.cdTelevisao.toString().includes(searchTerm))
        );
    }, [televisoes, searchTerm]);

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3 sm:gap-0">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Televisões</h1>
                    <p className="text-gray-600 mt-1 text-sm sm:text-base">Gerencie as televisões cadastradas</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                    <button
                        onClick={carregarTelevisoes}
                        disabled={loading}
                        className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 text-sm sm:text-base"
                    >
                        <RefreshCw size={18} className={`sm:w-[20px] sm:h-[20px] ${loading ? 'animate-spin' : ''}`} />
                        <span className="whitespace-nowrap">Atualizar</span>
                    </button>
                    <button
                        onClick={() => setShowLinkTotemModal(true)}
                        className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm sm:text-base"
                    >
                        <Link2 size={18} className="sm:w-[20px] sm:h-[20px]" />
                        <span className="whitespace-nowrap">Vincular ao Totem</span>
                    </button>
                    <button
                        onClick={() => {
                            setEditingId(null);
                            setFormData({ nmTelevisao: '' });
                            setShowForm(true);
                        }}
                        className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm sm:text-base"
                    >
                        <Plus size={18} className="sm:w-[20px] sm:h-[20px]" />
                        <span className="whitespace-nowrap">Nova Televisão</span>
                    </button>
                </div>
            </div>

            {/* Busca */}
            <div className="mb-4 sm:mb-6">
                <input
                    type="text"
                    placeholder="Buscar televisões..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full max-w-md px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
            </div>

            {/* Mensagem de erro */}
            {error && (
                <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm sm:text-base">
                    {error}
                </div>
            )}

            {/* Loading */}
            {loading && televisoes.length === 0 && (
                <div className="flex items-center justify-center py-8 sm:py-12">
                    <Loader className="animate-spin text-blue-600" size={24} />
                </div>
            )}

            {/* Lista de televisões */}
            {!loading && filteredTelevisoes.length === 0 && (
                <div className="text-center py-8 sm:py-12 text-gray-500 text-sm sm:text-base">
                    {searchTerm ? 'Nenhuma televisão encontrada' : 'Nenhuma televisão cadastrada'}
                </div>
            )}

            {!loading && filteredTelevisoes.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {filteredTelevisoes
                        .filter(tv => tv.cdTelevisao != null)
                        .map(televisao => (
                            <TelevisaoCard
                                key={televisao.cdTelevisao!}
                                televisao={televisao}
                                telasVinculadas={getTelasVinculadas(televisao)}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                onLinkScreens={handleLinkScreens}
                            />
                        ))}
                </div>
            )}

            {/* Modal de formulário */}
            {showForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-2 sm:mx-4">
                        <div className="p-4 sm:p-6 border-b flex justify-between items-center">
                            <h2 className="text-lg sm:text-xl font-bold text-gray-800">
                                {editingId ? 'Editar Televisão' : 'Nova Televisão'}
                            </h2>
                            <button
                                onClick={() => {
                                    setShowForm(false);
                                    setEditingId(null);
                                    setFormData({ nmTelevisao: '' });
                                }}
                                className="text-gray-500 hover:text-gray-700 p-1"
                            >
                                <X size={20} className="sm:w-[24px] sm:h-[24px]" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-4 sm:p-6">
                            <div className="mb-4">
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                                    Nome da Televisão
                                </label>
                                <input
                                    type="text"
                                    value={formData.nmTelevisao}
                                    onChange={(e) => setFormData({ nmTelevisao: e.target.value })}
                                    className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Ex: TV Loja 1"
                                    required
                                    disabled={saving || !!editingId}
                                />
                                {editingId && (
                                    <p className="mt-2 text-xs sm:text-sm text-gray-500">
                                        A edição do nome não está disponível no momento
                                    </p>
                                )}
                            </div>

                            <div className="flex flex-col sm:flex-row justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowForm(false);
                                        setEditingId(null);
                                        setFormData({ nmTelevisao: '' });
                                    }}
                                    className="px-3 sm:px-4 py-2 text-sm sm:text-base text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                                    >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving || !!editingId}
                                    className="px-3 sm:px-4 py-2 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {saving && <Loader className="animate-spin" size={14} />}
                                    <span>{editingId ? 'Salvar' : 'Criar'}</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal de vincular telas */}
            <LinkScreensModal
                televisao={selectedTelevisaoForLink}
                isOpen={!!selectedTelevisaoForLink}
                onClose={() => setSelectedTelevisaoForLink(null)}
                onSave={handleSaveLinkScreens}
            />

            {/* Modal de vincular ao totem */}
            {showLinkTotemModal && (
                <LinkTelevisaoModal
                    televisoes={televisoes}
                    onClose={() => setShowLinkTotemModal(false)}
                />
            )}
        </div>
    );
};

// Modal para linkar televisão ao totem
const LinkTelevisaoModal: React.FC<{
    televisoes: Televisao[];
    onClose: () => void;
}> = ({ televisoes, onClose }) => {
    const [inputMode, setInputMode] = useState<'manual' | 'qrcode'>('manual');
    const [showTelevisaoList, setShowTelevisaoList] = useState(false);
    const [selectedTelevisaoId, setSelectedTelevisaoId] = useState<number | null>(null);
    const [televisaoSearchTerm, setTelevisaoSearchTerm] = useState('');
    const [qrCodeSearchTerm, setQrCodeSearchTerm] = useState('');
    const [showTelevisaoDropdown, setShowTelevisaoDropdown] = useState(false);
    const [showCamera, setShowCamera] = useState(false);
    const [cameraLoading, setCameraLoading] = useState(false);
    const [scannerRunning, setScannerRunning] = useState(false);
    const [codigo, setCodigo] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<boolean>(false);
    const qrCodeScannerRef = useRef<Html5Qrcode | null>(null);
    const cameraContainerRef = useRef<HTMLDivElement>(null);
    const scannerRunningRef = useRef<boolean>(false);

    // Filtrar televisões por pesquisa
    const filteredTelevisoes = useMemo(() => {
        if (!televisaoSearchTerm.trim()) {
            return televisoes.filter(tv => tv.cdTelevisao != null);
        }
        const searchLower = televisaoSearchTerm.toLowerCase();
        return televisoes.filter(tv =>
            tv.nmTelevisao.toLowerCase().includes(searchLower) ||
            (tv.cdTelevisao != null && tv.cdTelevisao.toString().includes(searchLower))
        );
    }, [televisoes, televisaoSearchTerm]);

    // Filtrar televisões por pesquisa (modo QR code)
    const filteredTelevisoesQRCode = useMemo(() => {
        if (!qrCodeSearchTerm.trim()) return televisoes.filter(tv => tv.cdTelevisao != null);
        const searchLower = qrCodeSearchTerm.toLowerCase().trim();
        return televisoes.filter(tv =>
            tv.nmTelevisao?.toLowerCase().includes(searchLower) ||
            (tv.cdTelevisao != null && tv.cdTelevisao.toString().includes(searchLower))
        );
    }, [televisoes, qrCodeSearchTerm]);

    // Função para fazer o link da televisão
    const performLink = useCallback(async (codigoTotem: string, cdTelevisao: number) => {
        if (loading) {
            return;
        }

        // Obter lojaId do token atual
        const lojaId = authService.getLojaId();

        if (!lojaId) {
            setError('Não foi possível obter a loja. Por favor, faça login novamente.');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(false);

        try {
            // OPERAÇÃO TELEVISÃO: Enviar cdTelevisao no lugar de codigoDisplay
            // IMPORTANTE: Enviar cdTelevisao no lugar de codigoDisplay quando for televisão
            await bannerService.validarCodigoTelevisao(codigoTotem.trim(), cdTelevisao, lojaId, undefined, true);

            // NÃO fazer PUT - apenas validar o vínculo
            // O totem vai receber cdTelevisao na resposta e navegar para /display/televisao/:cdTelevisao

            setSuccess(true);

            // Limpar campos após sucesso
            setTimeout(() => {
                setCodigo('');
                setSuccess(false);
                setShowCamera(false);
                setShowTelevisaoList(false);
                setSelectedTelevisaoId(null);
                setTelevisaoSearchTerm('');
                setQrCodeSearchTerm('');
                setShowTelevisaoDropdown(false);
                setInputMode('manual');
                onClose();
            }, 3000);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erro ao vincular televisão');
        } finally {
            setLoading(false);
        }
    }, [loading, onClose]);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();

        if (!codigo.trim()) {
            setError('Código do totem é obrigatório');
            return;
        }

        if (!selectedTelevisaoId) {
            setError('Por favor, selecione uma televisão');
            return;
        }

        await performLink(codigo.trim(), selectedTelevisaoId);
    }, [codigo, selectedTelevisaoId, performLink]);

    // Função auxiliar segura para parar o scanner
    const safeStopScanner = useCallback(async (scanner: Html5Qrcode | null) => {
        if (!scanner) {
            return;
        }

        try {
            try {
                await scanner.stop();
            } catch (stopErr: any) {
                if (!stopErr.message?.includes('not running') && !stopErr.message?.includes('not paused')) {
                    console.warn('Erro ao parar scanner:', stopErr);
                }
            }

            try {
                scanner.clear();
            } catch (clearErr) {
                console.warn('Erro ao limpar scanner:', clearErr);
            }

            try {
                const elementId = 'qr-reader';
                const element = document.getElementById(elementId);
                if (element) {
                    const videoElements = element.querySelectorAll('video');
                    videoElements.forEach((video) => {
                        if (video.srcObject) {
                            const stream = video.srcObject as MediaStream;
                            stream.getTracks().forEach((track) => {
                                track.stop();
                            });
                            video.srcObject = null;
                        }
                    });
                    element.innerHTML = '';
                }
            } catch (cleanupErr) {
                console.warn('Erro ao limpar elemento DOM:', cleanupErr);
            }

            scannerRunningRef.current = false;
        } catch (err: any) {
            console.error('Erro geral ao parar scanner:', err);
            scannerRunningRef.current = false;
        }

        await new Promise(resolve => setTimeout(resolve, 100));
    }, []);

    // Função para iniciar a câmera e ler QR code (televisão)
    const startQRCodeScanTelevisao = useCallback(async (televisaoId: number) => {
        if (qrCodeScannerRef.current) {
            await safeStopScanner(qrCodeScannerRef.current);
            qrCodeScannerRef.current = null;
        }
        scannerRunningRef.current = false;
        setScannerRunning(false);
        setError(null);

        await new Promise(resolve => setTimeout(resolve, 200));

        setSelectedTelevisaoId(televisaoId);
        setShowTelevisaoList(false);
        setShowCamera(true);
    }, [safeStopScanner]);

    // useEffect para iniciar a câmera quando showCamera for true
    useEffect(() => {
        if (!showCamera || !selectedTelevisaoId) {
            return;
        }

        let scanner: Html5Qrcode | null = null;
        let isMounted = true;

        const startCamera = async () => {
            try {
                setCameraLoading(true);

                if (qrCodeScannerRef.current) {
                    await safeStopScanner(qrCodeScannerRef.current);
                    qrCodeScannerRef.current = null;
                }
                scannerRunningRef.current = false;

                await new Promise(resolve => setTimeout(resolve, 300));

                const elementId = 'qr-reader';
                let element = document.getElementById(elementId);
                let attempts = 0;
                const maxAttempts = 10;

                while (!element && attempts < maxAttempts) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                    element = document.getElementById(elementId);
                    attempts++;
                }

                if (!element) {
                    console.error('Elemento qr-reader não encontrado após', maxAttempts, 'tentativas');
                    if (isMounted) {
                        setError('Erro ao inicializar leitor de QR code. Tente novamente.');
                        setShowCamera(false);
                        setCameraLoading(false);
                    }
                    return;
                }

                element.innerHTML = '';

                scanner = new Html5Qrcode(elementId);
                qrCodeScannerRef.current = scanner;

                try {
                    const hostname = window.location.hostname;
                    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
                    const isLocalIP = /^(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[01])\.)/.test(hostname);
                    const isSecure = window.location.protocol === 'https:' || isLocalhost || isLocalIP;

                    if (!isSecure) {
                        console.warn('Acesso à câmera pode não funcionar sem HTTPS.');
                    }

                    try {
                        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                        stream.getTracks().forEach(track => track.stop());
                    } catch (permError: any) {
                        if (permError.name === 'NotAllowedError' || permError.name === 'PermissionDeniedError') {
                            throw new Error('PERMISSION_DENIED');
                        } else if (permError.name === 'NotFoundError' || permError.name === 'DevicesNotFoundError') {
                            throw new Error('NO_CAMERA');
                        } else {
                            throw new Error('CAMERA_ERROR');
                        }
                    }

                    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

                    let cameraId: string | null = null;
                    try {
                        const devices = await navigator.mediaDevices.enumerateDevices();
                        const videoDevices = devices.filter(device => device.kind === 'videoinput');

                        if (isMobile && videoDevices.length > 0) {
                            const backCamera = videoDevices.find(device =>
                                device.label.toLowerCase().includes('back') ||
                                device.label.toLowerCase().includes('rear') ||
                                device.label.toLowerCase().includes('environment')
                            );
                            cameraId = backCamera?.deviceId || videoDevices[videoDevices.length - 1].deviceId;
                        }
                    } catch (enumError) {
                        console.warn('Não foi possível listar câmeras:', enumError);
                    }

                    const config = isMobile
                        ? {
                            fps: 5,
                            qrbox: { width: 250, height: 250 },
                            aspectRatio: 1.0,
                            disableFlip: false
                        }
                        : {
                            fps: 10,
                            qrbox: { width: 250, height: 250 },
                            aspectRatio: 1.0
                        };

                    const cameraConfig = cameraId
                        ? { deviceId: { exact: cameraId } }
                        : { facingMode: 'environment' };

                    await scanner.start(
                        cameraConfig,
                        config,
                        (decodedText) => {
                            if (isMounted && scanner && scannerRunningRef.current) {
                                scannerRunningRef.current = false;
                                safeStopScanner(scanner).then(() => {
                                    qrCodeScannerRef.current = null;
                                    setShowCamera(false);
                                    setCameraLoading(false);
                                    setScannerRunning(false);
                                    setCodigo(decodedText);
                                    if (selectedTelevisaoId) {
                                        performLink(decodedText, selectedTelevisaoId);
                                    }
                                }).catch((err) => {
                                    console.error('Erro ao parar scanner após leitura:', err);
                                    if (isMounted) {
                                        qrCodeScannerRef.current = null;
                                        setShowCamera(false);
                                        setCameraLoading(false);
                                        setScannerRunning(false);
                                        setCodigo(decodedText);
                                        if (selectedTelevisaoId) {
                                            performLink(decodedText, selectedTelevisaoId);
                                        }
                                    }
                                });
                            }
                        },
                        () => { }
                    );

                    if (isMounted) {
                        scannerRunningRef.current = true;
                        setScannerRunning(true);
                        setCameraLoading(false);
                    }
                } catch (startError: any) {
                    console.error('Erro ao iniciar câmera:', startError);
                    if (isMounted) {
                        let errorMsg = 'Erro ao acessar a câmera.';
                        if (startError.message === 'PERMISSION_DENIED') {
                            errorMsg = 'Permissão da câmera negada. Por favor, permita o acesso à câmera nas configurações do navegador ou do dispositivo.';
                        } else if (startError.message === 'NO_CAMERA') {
                            errorMsg = 'Nenhuma câmera encontrada. Verifique se há uma câmera disponível no dispositivo.';
                        } else if (startError.message === 'CAMERA_ERROR') {
                            errorMsg = 'Erro ao acessar a câmera. Verifique se o dispositivo possui câmera e se não está sendo usada por outro aplicativo.';
                        }
                        setError(errorMsg);
                        setShowCamera(false);
                        setCameraLoading(false);
                    }
                }
            } catch (err: any) {
                console.error('Erro ao criar scanner:', err);
                if (isMounted) {
                    setError('Erro ao inicializar leitor de QR code. Tente novamente.');
                    setShowCamera(false);
                    setCameraLoading(false);
                }
            }
        };

        const timeoutId = setTimeout(() => {
            startCamera();
        }, 200);

        return () => {
            isMounted = false;
            clearTimeout(timeoutId);
            if (scanner) {
                safeStopScanner(scanner).catch(() => {});
            }
            scannerRunningRef.current = false;
            qrCodeScannerRef.current = null;
        };
    }, [showCamera, selectedTelevisaoId, performLink, safeStopScanner]);

    // Limpar scanner ao desmontar componente
    useEffect(() => {
        return () => {
            if (qrCodeScannerRef.current) {
                safeStopScanner(qrCodeScannerRef.current).catch(() => {});
                qrCodeScannerRef.current = null;
            }
            scannerRunningRef.current = false;
        };
    }, [safeStopScanner]);

    // Voltar para seleção de televisões (mantém modo QR code)
    const goBackToSelection = useCallback(async () => {
        if (qrCodeScannerRef.current) {
            await safeStopScanner(qrCodeScannerRef.current);
            qrCodeScannerRef.current = null;
        }

        const elementId = 'qr-reader';
        const element = document.getElementById(elementId);
        if (element) {
            element.innerHTML = '';
        }

        scannerRunningRef.current = false;
        setScannerRunning(false);
        setShowCamera(false);
        setCameraLoading(false);
        setError(null);
        setSelectedTelevisaoId(null);
        setShowTelevisaoList(true);
        setInputMode('qrcode');
    }, [safeStopScanner]);

    // Limpar scanner quando fechar a câmera
    const stopCamera = useCallback(async () => {
        if (qrCodeScannerRef.current) {
            await safeStopScanner(qrCodeScannerRef.current);
            qrCodeScannerRef.current = null;
        }

        const elementId = 'qr-reader';
        const element = document.getElementById(elementId);
        if (element) {
            element.innerHTML = '';
        }

        scannerRunningRef.current = false;
        setScannerRunning(false);
        setShowCamera(false);
        setSelectedTelevisaoId(null);
        setCameraLoading(false);
        setError(null);
        setInputMode('manual');
        setQrCodeSearchTerm('');
    }, [safeStopScanner]);

    // Fechar dropdowns ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('.dropdown-container')) {
                setShowTelevisaoDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Se está mostrando a câmera
    if (showCamera && selectedTelevisaoId) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg max-w-md w-full p-6">
                    <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={goBackToSelection}
                                className="text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-1"
                                title="Voltar para seleção"
                            >
                                <ChevronLeft size={20} />
                            </button>
                            <h2 className="text-xl font-bold text-gray-800">
                                Escanear QR Code
                            </h2>
                        </div>
                        <button
                            onClick={stopCamera}
                            className="text-gray-500 hover:text-gray-700 transition-colors"
                            title="Fechar"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    <div className="mb-4">
                        <p className="text-sm text-gray-600 mb-2">
                            Posicione o QR code dentro da área de leitura
                        </p>
                        {error && (
                            <div className="mb-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-red-700 text-sm mb-2">{error}</p>
                                {error.includes('Permissão') || error.includes('HTTPS') || error.includes('câmera') ? (
                                    <button
                                        onClick={async () => {
                                            setError(null);
                                            if (qrCodeScannerRef.current) {
                                                await safeStopScanner(qrCodeScannerRef.current);
                                                qrCodeScannerRef.current = null;
                                            }
                                            scannerRunningRef.current = false;
                                            setShowCamera(false);
                                            setTimeout(async () => {
                                                if (selectedTelevisaoId) {
                                                    await startQRCodeScanTelevisao(selectedTelevisaoId);
                                                }
                                            }, 500);
                                        }}
                                        className="text-sm text-red-700 underline hover:text-red-800"
                                    >
                                        Tentar novamente
                                    </button>
                                ) : null}
                            </div>
                        )}
                        {cameraLoading && (
                            <div className="mb-2 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-center gap-2">
                                <Loader size={20} className="animate-spin text-blue-600" />
                                <p className="text-blue-700 text-sm">Iniciando câmera...</p>
                            </div>
                        )}
                        {!error && !cameraLoading && (
                            <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <p className="text-yellow-700 text-xs">
                                    💡 Dica: Se a câmera não abrir, verifique as permissões do navegador nas configurações do dispositivo.
                                </p>
                            </div>
                        )}
                        <div
                            id="qr-reader"
                            ref={cameraContainerRef}
                            className="w-full rounded-lg overflow-hidden bg-black"
                            style={{ minHeight: '300px' }}
                        ></div>
                    </div>

                    <div className="flex gap-2">
                        <button
                            onClick={goBackToSelection}
                            className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2"
                        >
                            <ChevronLeft size={18} />
                            Voltar
                        </button>
                        {error && (
                            <button
                                onClick={async () => {
                                    setError(null);
                                    if (qrCodeScannerRef.current) {
                                        await safeStopScanner(qrCodeScannerRef.current);
                                        qrCodeScannerRef.current = null;
                                    }
                                    scannerRunningRef.current = false;
                                    setShowCamera(false);
                                    setTimeout(async () => {
                                        if (selectedTelevisaoId) {
                                            await startQRCodeScanTelevisao(selectedTelevisaoId);
                                        }
                                    }, 500);
                                }}
                                className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                            >
                                Tentar Novamente
                            </button>
                        )}
                        <button
                            onClick={stopCamera}
                            className={`px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 ${error ? 'flex-1' : 'flex-1'}`}
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Se está mostrando a lista de televisões (modo QR code)
    if (showTelevisaoList && inputMode === 'qrcode') {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col">
                    <div className="flex justify-between items-center p-4 border-b">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => {
                                    setShowTelevisaoList(false);
                                    setInputMode('manual');
                                    setQrCodeSearchTerm('');
                                }}
                                className="text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-1"
                                title="Voltar"
                            >
                                <ChevronLeft size={20} />
                            </button>
                            <h2 className="text-xl font-bold text-gray-800">
                                Selecione a Televisão
                            </h2>
                        </div>
                        <button
                            onClick={() => {
                                setShowTelevisaoList(false);
                                setInputMode('manual');
                                setQrCodeSearchTerm('');
                            }}
                            className="text-gray-500 hover:text-gray-700 transition-colors"
                            title="Fechar"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Barra de Pesquisa */}
                    <div className="p-4 border-b">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                value={qrCodeSearchTerm}
                                onChange={(e) => setQrCodeSearchTerm(e.target.value)}
                                placeholder="Pesquisar televisão por nome..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    <div className="overflow-y-auto p-4 flex-1">
                        {filteredTelevisoesQRCode.length === 0 ? (
                            <p className="text-gray-500 text-center py-8">
                                {qrCodeSearchTerm.trim() ? 'Nenhuma televisão encontrada' : 'Nenhuma televisão disponível'}
                            </p>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                {filteredTelevisoesQRCode.map((tv) => (
                                    <button
                                        key={tv.cdTelevisao}
                                        onClick={() => tv.cdTelevisao != null && startQRCodeScanTelevisao(tv.cdTelevisao)}
                                        disabled={loading || tv.cdTelevisao == null}
                                        className="p-2.5 border border-gray-200 rounded-md hover:border-blue-500 hover:bg-blue-50 transition-colors text-left disabled:opacity-50"
                                    >
                                        <div className="font-medium text-sm text-gray-800 truncate">{tv.nmTelevisao}</div>
                                        <div className="text-xs text-gray-400 mt-0.5">
                                            ID: {tv.cdTelevisao}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Modal principal
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">
                        Vincular ao Totem
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-700 text-sm">{error}</p>
                    </div>
                )}

                {success && (
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-green-700 font-medium">Validação concluída!</p>
                    </div>
                )}

                {/* Seleção de modo */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                        Como deseja vincular?
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                        <button
                            type="button"
                            onClick={() => {
                                setInputMode('manual');
                                setShowTelevisaoList(false);
                                setQrCodeSearchTerm('');
                            }}
                            className={`p-4 border-2 rounded-lg transition-colors ${inputMode === 'manual'
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                                }`}
                        >
                            <div className="flex flex-col items-center gap-2">
                                <span className="text-2xl">⌨️</span>
                                <span className="text-sm font-medium">Digitar Manualmente</span>
                            </div>
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setInputMode('qrcode');
                                setShowTelevisaoList(true);
                                setQrCodeSearchTerm('');
                            }}
                            className={`p-4 border-2 rounded-lg transition-colors ${inputMode === 'qrcode'
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                                }`}
                        >
                            <div className="flex flex-col items-center gap-2">
                                <QrCode size={24} />
                                <span className="text-sm font-medium">Ler QR Code</span>
                            </div>
                        </button>
                    </div>
                </div>

                {/* Formulário manual */}
                {inputMode === 'manual' && (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Código do Totem *
                            </label>
                            <input
                                type="text"
                                value={codigo}
                                onChange={(e) => {
                                    setCodigo(e.target.value);
                                    setError(null);
                                }}
                                className="w-full p-3 border border-gray-300 rounded-lg"
                                placeholder="Digite o código do totem"
                                disabled={loading}
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Selecione a Televisão *
                            </label>
                            <div className="relative dropdown-container">
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={televisaoSearchTerm}
                                        onChange={(e) => {
                                            setTelevisaoSearchTerm(e.target.value);
                                            setShowTelevisaoDropdown(true);
                                            setError(null);
                                        }}
                                        onFocus={() => setShowTelevisaoDropdown(true)}
                                        placeholder="Pesquisar ou selecionar televisão..."
                                        className="w-full p-3 border border-gray-300 rounded-lg pr-10"
                                        disabled={loading}
                                    />
                                    <Search size={20} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                </div>

                                {showTelevisaoDropdown && filteredTelevisoes.length > 0 && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                        {filteredTelevisoes.map((tv) => (
                                            <button
                                                key={tv.cdTelevisao}
                                                type="button"
                                                onClick={() => {
                                                    if (tv.cdTelevisao != null) {
                                                        setSelectedTelevisaoId(tv.cdTelevisao);
                                                        setTelevisaoSearchTerm(tv.nmTelevisao);
                                                        setShowTelevisaoDropdown(false);
                                                        setError(null);
                                                    }
                                                }}
                                                className={`w-full text-left px-4 py-3 hover:bg-gray-100 transition-colors ${selectedTelevisaoId === tv.cdTelevisao ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                                                    }`}
                                            >
                                                <div className="font-medium text-gray-800">{tv.nmTelevisao}</div>
                                                <div className="text-xs text-gray-400 mt-1">
                                                    ID: {tv.cdTelevisao}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {selectedTelevisaoId && (
                                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                                    <div className="text-sm font-medium text-blue-800">
                                        Televisão selecionada: {televisoes.find(tv => tv.cdTelevisao === selectedTelevisaoId)?.nmTelevisao}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={loading}
                                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <>
                                        <Loader size={18} className="animate-spin" />
                                        Linkando...
                                    </>
                                ) : (
                                    'Vincular'
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default TelevisoesManagementPage;
